const { HTMLElementExt } = await import(
    window._resolveImport?.("html-element-ext") || "html-element-ext"
);

const isPredictiveSearchApiSupported = () => {
    const shopifyFeatures =
        document.getElementById("shopify-features")?.innerHTML;

    // details at https://shopify.dev/docs/api/ajax/reference/predictive-search#supported-languages
    try {
        return JSON.parse(shopifyFeatures)?.predictiveSearch;
    } catch (error) {
        return false;
    }
};

class PredictiveSearch extends HTMLElementExt {
    constructor() {
        super();
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector(
            "[data-predictive-search]"
        );
        this.isOpen = false;
        this.handles = {
            "submit::form.search": this.onFormSubmit.bind(this),
            'input::input[type="search"]': debounce(
                this.onChange.bind(this),
                300
            ),
            'focus::input[type="search"]': this.onFocus.bind(this),
            focusout: this.onFocusOut.bind(this),
            keyup: this.onKeyup.bind(this),
            keydown: this.onKeydown.bind(this),
        };
    }

    connectedCallback() {
        if (isPredictiveSearchApiSupported()) {
            super.connectedCallback();

            // clean up onclick for submit button
            this.querySelectorAll("button[onclick]").forEach((button) =>
                button.removeAttribute("onclick")
            );

            // run search event when search field has focus and some value
            if (document.activeElement === this.input && this.getQuery())
                this.onChange();
        }
    }

    getQuery() {
        return this.input.value.trim();
    }

    getMinQueryLength() {
        return parseInt(this.dataset.minQueryLength);
    }

    onChange() {
        const searchTerm = this.getQuery();
        const minQueryLimit = this.getMinQueryLength();

        if (!searchTerm.length) {
            this.close(true);
            return;
        }

        this.getSearchResults(searchTerm, minQueryLimit);
    }

    onFormSubmit(event) {
        if (
            !this.getQuery().length ||
            this.querySelector('[aria-selected="true"] a')
        )
            event.preventDefault();
    }

    onFocus() {
        const searchTerm = this.getQuery();

        if (!searchTerm.length) return;

        if (this.getAttribute("results") === "true") {
            this.open();
        } else {
            this.getSearchResults(searchTerm);
        }
    }

    onFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
        });
    }

    onKeyup(event) {
        if (!this.getQuery().length) this.close(true);
        event.preventDefault();

        switch (event.code) {
            case "ArrowUp":
                this.switchOption("up");
                break;
            case "ArrowDown":
                this.switchOption("down");
                break;
            case "Enter":
                this.selectOption();
                break;
        }
    }

    onKeydown(event) {
        // Prevent the cursor from moving in the input when using the up and down arrow keys
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
            event.preventDefault();
        }
    }

    switchOption(direction) {
        if (!this.getAttribute("open")) return;

        const moveUp = direction === "up";
        const selectedElement = this.querySelector('[aria-selected="true"]');
        const allElements = this.querySelectorAll("li");
        let activeElement = this.querySelector("li");

        if (moveUp && !selectedElement) return;

        this.statusElement.textContent = "";

        if (!moveUp && selectedElement) {
            activeElement =
                selectedElement.nextElementSibling || allElements[0];
        } else if (moveUp) {
            activeElement =
                selectedElement.previousElementSibling ||
                allElements[allElements.length - 1];
        }

        if (activeElement === selectedElement) return;

        activeElement.setAttribute("aria-selected", true);
        if (selectedElement)
            selectedElement.setAttribute("aria-selected", false);

        this.setLiveRegionText(activeElement.textContent);
        this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
        const selectedProduct = this.querySelector(
            '[aria-selected="true"] a, [aria-selected="true"] button'
        );

        if (selectedProduct) selectedProduct.click();
    }

    getSearchResults(searchTerm, minQueryLimit = false) {
        /* min query length for appropriate search result */
        if (searchTerm.length < minQueryLimit) return;

        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        this.setLiveRegionLoadingState();

        this.predictiveSearchResults.innerHTML = "";
        fetchSectionHTML(
            "predictive-search",
            true,
            this.getPredictiveUrl(searchTerm)
        )
            .then((html) => {
                this.renderSearchResults(html);
            })
            .catch((error) => {
                this.close();
                throw error;
            });
    }

    getPredictiveUrl(term) {
        return (
            routes.predictive_search_url +
            [
                `?q=${encodeURIComponent(term)}`,
                `resources[type]=${this.dataset.types}`,
                "resources[limit]=6",
            ].join("&")
        );
    }

    setLiveRegionLoadingState() {
        this.statusElement =
            this.statusElement ||
            this.querySelector(".predictive-search-status");
        this.loadingText =
            this.loadingText || this.getAttribute("data-loading-text");

        this.setLiveRegionText(this.loadingText);
        this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;

        setTimeout(() => {
            this.statusElement.setAttribute("aria-hidden", "true");
        }, 1000);
    }

    renderSearchResults(resultsMarkup) {
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.setAttribute("results", true);
        this.predictiveSearchResults.dispatchEvent(
            new CustomEvent("html-update", {
                bubbles: true,
            })
        );

        this.open();
    }

    setLiveRegionResults() {
        this.removeAttribute("loading");
        this.setLiveRegionText(
            this.querySelector(
                "[data-predictive-search-live-region-count-value]"
            ).textContent
        );
    }

    getResultsMaxHeight() {
        const headerWrapper = this.closest(".header-wrapper");
        this.resultsMaxHeight =
            window.innerHeight - headerWrapper.getBoundingClientRect().bottom;
        return this.resultsMaxHeight;
    }

    open() {
        this.predictiveSearchResults.style.maxHeight =
            this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
        this.setAttribute("open", true);
        this.setLiveRegionResults();
        this.input.setAttribute("aria-expanded", true);
        this.isOpen = true;

        document.body.classList.add("overflow_hidden");
    }

    close(clearSearchTerm = false) {
        if (clearSearchTerm) {
            this.input.value = "";
            this.removeAttribute("results");
        }

        const selected = this.querySelector('[aria-selected="true"]');

        if (selected) selected.setAttribute("aria-selected", false);

        this.input.setAttribute("aria-activedescendant", "");
        this.removeAttribute("open");
        this.input.setAttribute("aria-expanded", false);
        this.resultsMaxHeight = false;
        this.predictiveSearchResults.removeAttribute("style");

        this.isOpen = false;

        document.body.classList.remove("overflow_hidden");
    }
}

export { PredictiveSearch };
